import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, LineChart, Line, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  Activity, Clock, Package, Wrench, TrendingUp, Users,
  RefreshCw, DollarSign, AlertTriangle, Shield, Gauge, Award,
  ArrowUpRight, ArrowDownRight, Timer, Loader2
} from 'lucide-react';

const COLORS = ['#0052CC', '#36B37E', '#FF5630', '#6554C0', '#FF8B00', '#00B8D9', '#EC4899', '#8B5CF6'];

const AdvancedAnalyticsPage = () => {
  const { selectedPlant } = useData();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('6');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { months: parseInt(months) };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      const res = await api.get('/analytics/advanced-analytics', { params });
      setData(res.data);
    } catch {
      toast.error('Eroare la incarcarea analiticelor');
    } finally {
      setLoading(false);
    }
  }, [months, selectedPlant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
    </div>
  );
  if (!data) return null;

  const { summary, mttr_mtbf, cost_trend, cost_by_section, top_parts, technicians, preventive_vs_corrective } = data;

  return (
    <div className="space-y-6 pb-24 lg:pb-6" data-testid="advanced-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            Analitice Avansate
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">MTTR/MTBF, costuri, consum piese, eficienta tehnicieni</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 luni</SelectItem>
              <SelectItem value="6">6 luni</SelectItem>
              <SelectItem value="12">12 luni</SelectItem>
              <SelectItem value="24">24 luni</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kpi-cards">
        {[
          { label: 'MTTR Mediu', value: `${summary.avg_mttr}h`, icon: Timer, color: 'text-blue-600', bg: 'bg-blue-50', desc: 'Timp mediu reparatie' },
          { label: 'MTBF Mediu', value: `${summary.avg_mtbf}h`, icon: Shield, color: 'text-emerald-600', bg: 'bg-emerald-50', desc: 'Timp mediu intre defecte' },
          { label: 'Cost Total', value: `${(summary.total_cost / 1000).toFixed(1)}k`, icon: DollarSign, color: 'text-amber-600', bg: 'bg-amber-50', desc: `Mat: ${(summary.total_material_cost/1000).toFixed(1)}k + Man: ${(summary.total_labor_cost/1000).toFixed(1)}k` },
          { label: 'Defecte', value: summary.total_failures, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', desc: `${summary.total_tickets} tichete total` },
          { label: 'Tehnicieni', value: summary.technicians_active, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50', desc: `${summary.preventive_done} preventive` },
        ].map((kpi, i) => (
          <Card key={i} className="border-0 shadow-sm">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-8 h-8 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                </div>
                <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">{kpi.label}</p>
              </div>
              <p className={`text-2xl font-black ${kpi.color}`} data-testid={`kpi-${i}`}>{kpi.value}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{kpi.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Row 1: MTTR/MTBF Chart + Preventive vs Corrective */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* MTTR/MTBF per Equipment */}
        <Card className="lg:col-span-2 border-0 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Gauge className="w-4 h-4 text-blue-500" /> MTTR / MTBF per Echipament (Top 10)
            </h3>
            {mttr_mtbf.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={mttr_mtbf.slice(0, 10)} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={95} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="mttr_hours" name="MTTR (ore)" fill="#ef4444" radius={[0, 4, 4, 0]} barSize={10} />
                  <Bar dataKey="mtbf_hours" name="MTBF (ore)" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 py-8 text-center">Nicio data disponibila</p>}
          </CardContent>
        </Card>

        {/* Preventive vs Corrective */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-purple-500" /> Preventiva vs Corectiva
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Preventiva', value: preventive_vs_corrective.preventive },
                    { name: 'Corectiva', value: preventive_vs_corrective.corrective || summary.total_tickets }
                  ]}
                  cx="50%" cy="50%" outerRadius={80} innerRadius={40}
                  paddingAngle={5} dataKey="value" label={({ name, value }) => `${name}: ${value}`}
                >
                  <Cell fill="#36B37E" />
                  <Cell fill="#FF5630" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-center mt-2">
              <p className="text-xs text-slate-500">Raport: <span className="font-bold text-slate-800">{preventive_vs_corrective.ratio}:1</span></p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Cost Trend + Cost by Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-500" /> Trend Cost Mentenanta (lunar)
            </h3>
            {cost_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={cost_trend}>
                  <defs>
                    <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v.toFixed(0)} RON`, 'Cost']} contentStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="cost" stroke="#f59e0b" fill="url(#costGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 py-8 text-center">Nicio data</p>}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" /> Cost per Sectie
            </h3>
            {cost_by_section.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={cost_by_section.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="section" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v.toFixed(0)} RON`]} contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="cost" fill="#10b981" radius={[6, 6, 0, 0]}>
                    {cost_by_section.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 py-8 text-center">Nicio data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Top Parts Consumed */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Package className="w-4 h-4 text-blue-500" /> Top 10 Piese Consumate (trend lunar)
          </h3>
          {top_parts.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top_parts.slice(0, 10)} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={115} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" fill="#3b82f6" radius={[0, 6, 6, 0]} barSize={14}>
                    {top_parts.slice(0, 10).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {top_parts[0]?.trend?.length > 1 && (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={(() => {
                    const allMonths = new Set();
                    top_parts.slice(0, 5).forEach(p => p.trend?.forEach(t => allMonths.add(t.month)));
                    const months = [...allMonths].sort();
                    return months.map(m => {
                      const point = { month: m };
                      top_parts.slice(0, 5).forEach(p => {
                        const found = p.trend?.find(t => t.month === m);
                        point[p.name] = found?.qty || 0;
                      });
                      return point;
                    });
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 10 }} />
                    <Legend wrapperStyle={{ fontSize: 9 }} />
                    {top_parts.slice(0, 5).map((p, i) => (
                      <Line key={p.name} type="monotone" dataKey={p.name} stroke={COLORS[i]} strokeWidth={2} dot={{ r: 2 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          ) : <p className="text-xs text-slate-400 py-8 text-center">Nicio data</p>}
        </CardContent>
      </Card>

      {/* Row 4: Technician Efficiency */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-500" /> Eficienta Tehnicieni
          </h3>
          {technicians.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="tech-table">
                <thead>
                  <tr className="border-b text-slate-500">
                    <th className="text-left py-2 font-semibold">#</th>
                    <th className="text-left py-2 font-semibold">Tehnician</th>
                    <th className="text-center py-2 font-semibold">Atribuite</th>
                    <th className="text-center py-2 font-semibold">Finalizate</th>
                    <th className="text-center py-2 font-semibold">Rata</th>
                    <th className="text-center py-2 font-semibold">Timp Med.</th>
                    <th className="text-center py-2 font-semibold">SLA</th>
                    <th className="text-center py-2 font-semibold">Scor</th>
                  </tr>
                </thead>
                <tbody>
                  {technicians.map((t, i) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tech-row-${i}`}>
                      <td className="py-2.5">
                        {i < 3 ? (
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : 'bg-amber-700'}`}>{i + 1}</span>
                        ) : <span className="text-slate-400 pl-1.5">{i + 1}</span>}
                      </td>
                      <td className="py-2.5 font-semibold text-slate-800">{t.name}</td>
                      <td className="text-center">{t.assigned}</td>
                      <td className="text-center font-semibold text-emerald-600">{t.completed}</td>
                      <td className="text-center">
                        <Badge className={`text-[9px] ${t.completion_rate >= 80 ? 'bg-emerald-100 text-emerald-700' : t.completion_rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {t.completion_rate}%
                        </Badge>
                      </td>
                      <td className="text-center text-slate-600">{t.avg_hours}h</td>
                      <td className="text-center">
                        <Badge className={`text-[9px] ${t.sla_rate >= 80 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {t.sla_rate}%
                        </Badge>
                      </td>
                      <td className="text-center">
                        <span className={`text-sm font-black ${t.score >= 70 ? 'text-emerald-600' : t.score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                          {t.score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-xs text-slate-400 py-8 text-center">Nicio data</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedAnalyticsPage;
